import React from 'react'
import { AlertTriangle, Target } from 'lucide-react'
import Gauge from './ui/Gauge'
import { formatCurrency, formatPrice, formatSignedCurrency, formatSignedPercent, formatPercent, formatNumber } from '../utils/format'
import { toneColor } from '../utils/tone'
import { PRIVACY } from '../config/ondsConfig'

const MASK = PRIVACY.mask

export default function PortfolioPanel({ metrics, locked = false }) {
  const show = !locked && !!metrics
  const s = (value) => (show ? value : MASK)

  const pnlTone = show && metrics.unrealizedPnl >= 0 ? 'bull' : show ? 'bear' : 'neutral'
  const headroom = show ? metrics.marginHeadroom : 0
  const headroomTone = !show ? 'neutral' : headroom > 0.35 ? 'bull' : headroom > 0.2 ? 'caution' : 'bear'
  const goalProgress = show ? metrics.goalProgress : 0

  return (
    <div className="onds-card p-5 md:p-6">
      <div className="flex items-center justify-between mb-5">
        <h2 className="text-lg font-bold">Portfolio Command</h2>
        <span className="onds-chip" style={{ color: 'var(--onds-text-dim)' }}>
          {s(`${formatNumber(metrics?.shares)} sh @ ${formatPrice(metrics?.avgCost)}`)}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-3 mb-4">
        <Metric label="Market Value" value={s(formatCurrency(metrics?.marketValue))} />
        <Metric label="Cost Basis" value={s(formatCurrency(metrics?.costBasis))} />
        <Metric
          label="Unrealized P/L"
          value={s(`${formatSignedCurrency(metrics?.unrealizedPnl)} (${formatSignedPercent(metrics?.unrealizedPnlRatio)})`)}
          tone={pnlTone}
        />
        <Metric label="Tax If Sold (25%)" value={s(formatCurrency(metrics?.taxLiability))} tone={show ? 'caution' : 'neutral'} />
      </div>

      {metrics?.holdings?.length > 1 && (
        <div className="rounded-xl p-3 mb-6" style={{ backgroundColor: 'var(--onds-surface)' }}>
          <div className="text-[11px] uppercase tracking-wider text-[var(--onds-text-dim)] mb-2">Holdings</div>
          <div className="flex flex-col gap-1.5">
            {metrics.holdings.map((h) => (
              <div key={h.symbol} className="flex items-center justify-between gap-2 text-sm">
                <span className="flex items-center gap-1.5 min-w-0">
                  <span className="font-bold">{h.symbol}</span>
                  {h.primary && <span className="onds-chip text-[10px]" style={{ color: 'var(--onds-accent)' }}>primary</span>}
                  <span className="text-[var(--onds-text-dim)] truncate">{s(`${formatNumber(h.shares)} @ ${formatPrice(h.avgCost)}`)}</span>
                </span>
                <span className="flex items-center gap-3 shrink-0 tabular-nums">
                  <span>{s(formatCurrency(h.marketValue))}</span>
                  <span style={{ color: toneColor(!show ? 'neutral' : h.unrealizedPnl >= 0 ? 'bull' : 'bear') }}>
                    {s(formatSignedPercent(h.unrealizedPnlRatio))}
                  </span>
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6 items-center">
        <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--onds-surface)' }}>
          <div className="flex items-center gap-2 mb-2">
            <AlertTriangle className="h-4 w-4" style={{ color: toneColor(headroomTone) }} />
            <span className="text-sm font-semibold">Margin-Call Buffer</span>
          </div>
          <Gauge
            value={headroom}
            color={toneColor(headroomTone)}
            label={s(formatPercent(headroom, 0))}
            caption={show ? `Call price ${formatPrice(metrics.marginCallPrice)} · now ${formatPrice(metrics.price)}` : `Call price ${MASK}`}
          />
          <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
            <KV k="Margin Debt" v={s(formatCurrency(metrics?.marginDebt))} />
            <KV k="Personal Loan" v={s(formatCurrency(metrics?.personalLoan))} />
          </div>
        </div>

        <div className="rounded-xl p-4" style={{ backgroundColor: 'var(--onds-surface)' }}>
          <div className="flex items-center gap-2 mb-3">
            <Target className="h-4 w-4" style={{ color: 'var(--onds-accent)' }} />
            <span className="text-sm font-semibold">Road to {s(formatCurrency(metrics?.netWorthGoal))} Net</span>
          </div>
          <div className="flex items-baseline justify-between mb-1">
            <span className="text-2xl font-bold" style={{ color: toneColor(show ? 'bull' : 'neutral') }}>
              {s(formatCurrency(metrics?.netWorth))}
            </span>
            <span className="text-sm font-semibold text-[var(--onds-text-dim)]">
              {s(formatPercent(goalProgress, 1))}
            </span>
          </div>
          <div className="h-3 rounded-full overflow-hidden mb-2" style={{ backgroundColor: 'var(--onds-border)' }}>
            <div
              className="h-full rounded-full transition-all duration-700"
              style={{
                width: `${Math.max(show ? 2 : 0, goalProgress * 100)}%`,
                background: 'linear-gradient(90deg, var(--onds-accent), var(--onds-bull))',
              }}
            />
          </div>
          <p className="text-xs text-[var(--onds-text-dim)]">
            Net after debt and tax. Gap to target: <span className="font-semibold text-[var(--onds-text)]">{s(formatCurrency(metrics?.goalGap))}</span>
          </p>
        </div>
      </div>
    </div>
  )
}

function Metric({ label, value, tone = 'neutral' }) {
  return (
    <div className="rounded-xl p-3" style={{ backgroundColor: 'var(--onds-surface)' }}>
      <div className="text-[11px] uppercase tracking-wider text-[var(--onds-text-dim)]">{label}</div>
      <div className="text-base font-bold tabular-nums mt-0.5" style={{ color: toneColor(tone) }}>{value}</div>
    </div>
  )
}

function KV({ k, v }) {
  return (
    <div className="flex justify-between">
      <span className="text-[var(--onds-text-dim)]">{k}</span>
      <span className="font-semibold tabular-nums">{v}</span>
    </div>
  )
}
